/**
 * Assistant query store
 *
 * Holds the in-flight + landed payloads for the conversational chat surface.
 * Each query opens a "transient" smart-collection tab, identified by a
 * client-generated `transient:<uuid>` id. The tab content reads from this
 * store; the regular tabs store (`app-tabs-store`) just remembers the
 * transient id is open and what its title is.
 *
 * Persisted to sessionStorage so a hard reload preserves a transient tab's
 * payload alongside `app.openCollectionTabs`. Cross-session is intentionally
 * not preserved — these are ephemeral until the user clicks Save.
 */
import { useSyncExternalStore } from "react";
import { ConfiguredLogger } from "@/lib/logger";
const logger = new ConfiguredLogger({ caller: "AssistantQueryStore" });
const STORAGE_KEY = "app.assistantQueryTabs";
// URL-safe prefix — no colon, so it survives `location.pathname` matching
// in AppNav and doesn't need encodeURIComponent in the route.
const TRANSIENT_PREFIX = "qry-";
let _state = loadFromStorage();
const _listeners = new Set();
function loadFromStorage() {
    if (typeof window === "undefined")
        return {};
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return {};
        // Normalize: anything still "loading" from a previous session is dead
        const normalized = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (v && typeof v === "object" && typeof v.query === "string") {
                normalized[k] = v.status === "loading" ? { ...v, status: "error", errorMessage: "Lost on reload" } : v;
            }
        }
        return normalized;
    }
    catch (e) {
        logger.warn("Failed to load assistant query store", {
            error: e instanceof Error ? e.message : String(e),
        });
        return {};
    }
}
function saveToStorage() {
    if (typeof window === "undefined")
        return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_state));
    }
    catch (e) {
        logger.warn("Failed to persist assistant query store", {
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
function notify() {
    for (const l of _listeners)
        l();
}
function subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}
function getSnapshot() {
    return _state;
}
/** Hook: get the state for a specific transient id (or undefined). */
export function useAssistantQueryTab(transientId) {
    const all = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    if (!transientId)
        return undefined;
    return all[transientId];
}
export function isTransientId(id) {
    return id.startsWith(TRANSIENT_PREFIX);
}
export function newTransientId() {
    // crypto.randomUUID is available in modern browsers and Node; fall back to a
    // timestamp-based id otherwise.
    const uuid = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return `${TRANSIENT_PREFIX}${uuid}`;
}
export function startQuery(transientId, query) {
    _state = { ..._state, [transientId]: { status: "loading", query } };
    saveToStorage();
    notify();
}
export function setQueryReady(transientId, payload) {
    const existing = _state[transientId];
    if (!existing)
        return;
    _state = {
        ..._state,
        [transientId]: { ...existing, status: "ready", payload, errorMessage: undefined },
    };
    saveToStorage();
    notify();
}
export function setQueryError(transientId, errorMessage) {
    const existing = _state[transientId];
    if (!existing)
        return;
    _state = {
        ..._state,
        [transientId]: { ...existing, status: "error", errorMessage },
    };
    saveToStorage();
    notify();
}
export function discardQuery(transientId) {
    if (!(transientId in _state))
        return;
    const next = { ..._state };
    delete next[transientId];
    _state = next;
    saveToStorage();
    notify();
}
